import { AlarmCategoryList, AlarmType, DeviceStat } from './alarm-dashboard.component';

// ─────────────────────────────────────────────────────────────────────────────
// Raw shape from the counts API
// ─────────────────────────────────────────────────────────────────────────────

export interface CountRow {
  device_type: string;   // "METER" | "DIC" | "DL" | …
  alarm_id:    string;   // alarm type label exactly as the API sends it
  count:       string | number;
  site_count:  string | number;
  severity:    string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function toNum(v: string | number): number {
  return typeof v === 'number' ? v : parseInt(v, 10) || 0;
}

// ── Categorisation sets (kept in sync with alarm-dashboard-mapper.ts) ──────

let GRID_ALARM_IDS = new Set();

let DG_ALARM_IDS = new Set();
export function initAlarmCategories(list: AlarmCategoryList): void {
  GRID_ALARM_IDS = new Set(list.GRID.map(id => id.trim().toUpperCase()));
  DG_ALARM_IDS   = new Set(list.DG.map(id => id.trim().toUpperCase())); 
}
/**
 * Canonical alarm-type normalisation.
 *
 * The API mixes casing / phrasing (e.g. "Low PF alerts(B Phase)", "timeout",
 * "DG-OVERLOAD-NORMAL").  Normalise before categorising so counts land in the
 * right bucket.  Extend this map whenever the API introduces new labels.
 */
const ALARM_LABEL_NORMALISE: Record<string, string> = {
  '121':                            'ALERT_GRID_READING_GREATER_THAN_THRESHOLD ',
  '122':                            'ALERT_DG_READING_GREATER_THAN_THRESHOLD '
};

function normaliseAlarmId(raw: string): string {
  return ALARM_LABEL_NORMALISE[raw.toLowerCase()] ?? raw;
}

function alarmCat(normalisedId: string): 'grid' | 'dg' | 'others' {
  const u = normalisedId.toUpperCase();
  if (GRID_ALARM_IDS.has(u)) return 'grid';
  if (DG_ALARM_IDS.has(u))   return 'dg';
  return 'others';
}


// ── Device colour pool ───────────────────────────────────────────────────────

const DEVICE_COLOR_POOL = [
  '#3b82f6',  // blue   — METER
  '#f59e0b',  // amber  — DIC
  '#a78bfa',  // purple — DL
  '#2dd4bf',  // teal
  '#22c55e',  // green
  '#f472b6',  // pink
  '#fb923c',  // orange
  '#94a3b8',  // slate
];

const DEVICE_DISPLAY_MAP: Record<string, string> = {
  METER: 'Meter',
  DIC:   'CB',
  DL:    'Gateway',
  // extend as needed
};

function isUnmappedDeviceId(raw: string | null | undefined): boolean {

  if (!raw) {
    return false;
  }

  return /^[\d\-.:_]+$/.test(raw.trim());

}
function deviceDisplay(raw: string | null | undefined): string {

  const value = raw?.trim();

  if (!value) {
    return 'Unknown';
  }

  if (isUnmappedDeviceId(value)) {
    return 'Unmapped';
  }

  return DEVICE_DISPLAY_MAP[value.toUpperCase()] ?? value;

}

// ─────────────────────────────────────────────────────────────────────────────
// 1.  STAT CARDS  — top summary numbers
//
// Cards produced:
//   Total alarms | Grid alarms | DG alarms | Other alarms |
//   No meter response | Low PF | Grid failures | Maintenance mode
// ─────────────────────────────────────────────────────────────────────────────

export interface AlarmCountsFromApi {
  total:       number;
  grid:        number;
  dg:          number;
  others:      number;
  noMeter:     number;
  lowPF:       number;
  gridFail:    number;
  maintenance: number;
}

/**
 * Collects alarm IDs from count rows into categorized lists
 */
export function collectAlarmIdLists(rows: CountRow[]): {
  gridList: string[];
  dgList: string[];
  noMeterList: string[];
  lowPFList: string[];
  gridFailList: string[];
  maintenanceList: string[];
  othersList: string[];
} {
  const gridSet = new Set<string>();
  const dgSet = new Set<string>();
  const noMeterSet = new Set<string>();
  const lowPFSet = new Set<string>();
  const gridFailSet = new Set<string>();
  const maintenanceSet = new Set<string>();
  const categorizedSet = new Set<string>(); // Track all categorized alarms
  
  for (const row of rows) {
    const n = normaliseAlarmId(row.alarm_id);
    const cat = alarmCat(n);
    const upper = n.toUpperCase();
    const count = toNum(row.count);
    
    // Skip if count is 0
    if (count === 0) continue;
    
    // Categorize into primary categories (grid/dg)
    if (cat === 'grid' && upper !== 'GRID FAIL') {
      gridSet.add(upper);
      categorizedSet.add(upper);
    } else if (cat === 'dg') {
      dgSet.add(upper);
      categorizedSet.add(upper);
    }
    
    // Specific card lists (these are independent)
    if (upper === 'NO METER RESPONSE') {
      noMeterSet.add(upper);
      categorizedSet.add(upper);
    }
    
    if (upper.includes('PF') || upper.includes('LOW PF')) {
      lowPFSet.add(upper);
      categorizedSet.add(upper);
    }
    
    if (upper === 'GRID FAIL') {
      gridFailSet.add(upper);
      categorizedSet.add(upper);
    }
    
    if (upper.includes('MAINTENANCE')) {
      maintenanceSet.add(upper);
      categorizedSet.add(upper);
    }
  }
  
  // Others = all alarm IDs not in any specific category
  const othersSet = new Set<string>();
  for (const row of rows) {
    const n = normaliseAlarmId(row.alarm_id);
    const upper = n.toUpperCase();
    const count = toNum(row.count);
    
    if (count > 0 && !categorizedSet.has(upper)) {
      othersSet.add(upper);
    }
  }
  
  return {
    gridList: Array.from(gridSet).sort(),
    dgList: Array.from(dgSet).sort(),
    noMeterList: Array.from(noMeterSet).sort(),
    lowPFList: Array.from(lowPFSet).sort(),
    gridFailList: Array.from(gridFailSet).sort(),
    maintenanceList: Array.from(maintenanceSet).sort(),
    othersList: Array.from(othersSet).sort(),
  };
}

/**
 * Aggregates all rows into the 8 summary numbers used by the stat cards.
 */
export function deriveCountsFromRows(rows: CountRow[]): AlarmCountsFromApi {
  let grid = 0, dg = 0, others = 0,
      noMeter = 0, lowPF = 0, gridFail = 0, maintenance = 0;

  for (const row of rows) {
    const n     = normaliseAlarmId(row.alarm_id);
    const cat   = alarmCat(n);
    const count = toNum(row.count);
    const upper = n.toUpperCase();

    if      (cat === 'grid' && upper !== 'GRID FAIL') grid += count;
    else if (cat === 'dg')   dg   += count;
    else                     others += count;
    // Specific card counters — independent tallies, not subtracted from others
    if (upper === 'NO METER RESPONSE')                        noMeter     += count;
    if (upper.includes('PF') || upper.includes('LOW PF'))     lowPF       += count;
    if (upper === 'GRID FAIL')                                gridFail    += count;
    if (upper.includes('MAINTENANCE'))                        maintenance += count;
  }
  
  others = others-lowPF-noMeter-gridFail-maintenance
  const total = rows.reduce((s, r) => s + toNum(r.count), 0);

  return { total, grid, dg, others, noMeter, lowPF, gridFail, maintenance };
}

/**
 * Builds the statCards array from count rows.
 * Pass `includeSitesCard = true` when NOT on a single-site view.
 */
export function buildStatCardsFromCountRows(
  rows:             CountRow[],
  includeSitesCard  = false,
  totalSites        = 0,
): any[] {
  const c = deriveCountsFromRows(rows);
  const lists = collectAlarmIdLists(rows);

  const cards: any[] = [
    {
      id: 1, 
      title: 'Total alarms',      
      clicktype: 'scroll',
      tableAPI: '', // Total alarms has no filter list
      focus: 'site_list',
      total: c.total,
      Positive_Name: '',                  
      show: false, 
      key: 'total-alarms',
      class1: 'total-Complete border-b-sensorcolor',  
      class2: 'sensorcolor',
      imagepath: 'assets/icons/bell.png',
    },
    {
      id: 2, 
      title: 'Grid alarms',        
      clicktype: 'table',
      tableAPI: lists.gridList.join(','), // Grid alarm IDs (excludes GRID FAIL)
      focus: '',
      total: c.grid,
      Positive_Name: 'FAIL · OVERLOAD · MF', 
      show: false, 
      key: 'grid-alarms',
      class1: 'total-Complete border-b-cbcolor', 
      class2: 'cbcolor',
      imagepath: 'assets/icons/GRID.png',
    },
    {
      id: 3, 
      title: 'DG alarms',          
      clicktype: 'table',
      tableAPI: lists.dgList.join(','), // DG alarm IDs
      focus: '',
      total: c.dg,
      Positive_Name: 'ON/OFF · THRESHOLD · MF', 
      show: false, 
      key: 'dg-alarms',
      class1: 'total-Complete border-b-dlcolor', 
      class2: 'dlcolor',
      imagepath: 'assets/icons/DG.png',
    },
    {
      id: 5, 
      title: 'No meter response',  
      clicktype: 'table',
      tableAPI: lists.noMeterList.join(','), // NO METER RESPONSE
      focus: '',
      total: c.noMeter,
      Positive_Name: 'Critical · METER devices', 
      show: true, 
      key: 'no-meter',
      class1: 'total-Complete border-b-siteBorderColor', 
      class2: 'siteBorderColor',
      imagepath: 'assets/icons/no-signal.png',
    },
    {
      id: 6, 
      title: 'Low PF alerts',      
      clicktype: 'table',
      tableAPI: lists.lowPFList.join(','), // PF-related alarm IDs
      focus: '',
      total: c.lowPF,
      Positive_Name: 'PF < 0.3 threshold', 
      show: true, 
      key: 'low-pf',
      class1: 'total-Complete border-b-rechargemodecolor', 
      class2: 'rechargemodecolor',
      imagepath: 'assets/icons/low-price.png',
    },
    {
      id: 7, 
      title: 'Grid failures',      
      clicktype: 'table',
      tableAPI: lists.gridFailList.join(','), // GRID FAIL
      focus: '',
      total: c.gridFail,
      Positive_Name: 'GRID FAIL events',  
      show: true, 
      key: 'grid-fail',
      class1: 'total-Complete border-b-dlcolor', 
      class2: 'dlcolor',
      imagepath: 'assets/icons/failure.png',
    },
    {
      id: 8, 
      title: 'Maintenance mode',   
      clicktype: 'table',
      tableAPI: lists.maintenanceList.join(','), // MAINTENANCE-related alarm IDs
      focus: '',
      total: c.maintenance,
      Positive_Name: 'SITE + DIC combined', 
      show: true, 
      key: 'maintenance',
      class1: 'total-Complete border-b-sensorcolor', 
      class2: 'sensorcolor',
      imagepath: 'assets/icons/settings.png',
    },
    {
      id: 4, 
      title: 'Other alarms',       
      clicktype: 'table',
      tableAPI: lists.othersList.join(','), // All uncategorized alarm IDs
      focus: '',
      total: c.others,
      Positive_Name: 'PF · TIMEOUT · SENSOR', 
      show: false, 
      key: 'other-alarms',
      class1: 'total-Complete border-b-wqqtycolor', 
      class2: 'wqqtycolor',
      imagepath: 'assets/icons/alarm.png',
    },
  ];

  if (includeSitesCard) {
    cards.unshift({
      id: 0, 
      title: 'Total Sites', 
      total: totalSites,
      Positive_Name: '', 
      show: false, 
      key: '',
      class1: 'total-project border-b-siteBorderColor', 
      class2: 'siteBorderColor',
      imagepath: 'assets/DashboardIcon/site.png',
    });
  }

  return cards;
}

// ─────────────────────────────────────────────────────────────────────────────
// 2.  DEVICE STATS  — "By device type" bar chart + mini legend
//
// Aggregates count per device_type across all alarm_ids.
// ─────────────────────────────────────────────────────────────────────────────

export function buildDeviceStatsFromCountRows(rows: CountRow[]): DeviceStat[] {
  // Sum counts per device_type
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = deviceDisplay(row.device_type);
    totals.set(key, (totals.get(key) ?? 0) + toNum(row.count));
  }

  const grandTotal = [...totals.values()].reduce((s, v) => s + v, 0) || 1;

  // Fixed display order; any new device type appended at the end
  const FIXED = ['Meter', 'CB', 'Gateway'];
  const dynamic = [...totals.keys()].filter(k => !FIXED.includes(k)).sort();
  const ORDER = [...FIXED, ...dynamic].filter(k => totals.has(k));

  return ORDER.map((name, i) => {
    const count = totals.get(name)!;
    return {
      name,
      count,
      pct:   Math.round((count / grandTotal) * 100),
      color: DEVICE_COLOR_POOL[i % DEVICE_COLOR_POOL.length],
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 3.  ALARM TYPE BARS  — horizontal bar chart (top alarm types by frequency)
//
// Each unique alarm_id in the counts list becomes one bar, with count summed
// across all device_types that share that alarm_id.
// ─────────────────────────────────────────────────────────────────────────────
function resolveAlarmSev(label: string, rawSev: string): AlarmType['sev'] {
  const sev   = rawSev.toUpperCase();
  const upper = label.toUpperCase();

  if (sev === '1-CRITICAL' || upper.includes('VOLTAGE') || upper.includes('PF')) return 'high';
  if (sev === 'HIGH') return 'med';
  return 'low';
}

export function buildTypeBarsFromCountRows(rows: CountRow[]): AlarmType[] {
  const totals   = new Map<string, number>();
  const severity = new Map<string, string>();

  for (const row of rows) {
    const label  = normaliseAlarmId(row.alarm_id);
    totals.set(label, (totals.get(label) ?? 0) + toNum(row.count));

    const rawSev = String(row.severity ?? '').trim();
    // Promote to highest severity seen for this label
    const current = severity.get(label) ?? '';
    if (!severity.has(label) || isSevHigher(rawSev, current)) {
      severity.set(label, rawSev);
    }
  }

  return Array.from(totals.entries()).map(([label, count]) => ({
    label,
    count,
    cat: alarmCat(label),
    sev: resolveAlarmSev(label, severity.get(label) ?? ''),
  }));
}

// '1-CRITICAL' > 'HIGH' > anything else
function isSevHigher(incoming: string, existing: string): boolean {
  const rank = (s: string) =>
    s.toUpperCase() === '1-CRITICAL' ? 2 :
    s.toUpperCase() === 'HIGH'       ? 1 : 0;
  return rank(incoming) > rank(existing);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4.  PIE / DONUT DATA  — category split chart
//
// Mirrors the existing mapAlarmsToPieData but takes CountRow[] instead of
// RecentAlarm[].  Pass the same pieCategories array from the component.
// ─────────────────────────────────────────────────────────────────────────────

export interface PieSlice { label: string; value: number; color: string; }

export function buildPieDataFromCountRows(
  rows:       CountRow[],
  categories: Array<{ label: string; color: string }>,
): PieSlice[] {
  let noResp = 0, gridCount = 0, dgCount = 0, lowPf = 0,
      timeout = 0, maint = 0, other = 0;

  for (const row of rows) {
    const n     = normaliseAlarmId(row.alarm_id);
    const upper = n.toUpperCase();
    const count = toNum(row.count);

    if (upper === 'NO METER RESPONSE')               { noResp    += count; continue; }
    if (GRID_ALARM_IDS.has(upper))                   { gridCount += count; continue; }
    if (DG_ALARM_IDS.has(upper))                     { dgCount   += count; continue; }
    if (upper.includes('PF') || upper.includes('LOW PF')) { lowPf += count; continue; }
    if (upper.includes('TIMEOUT'))                   { timeout   += count; continue; }
    if (upper.includes('MAINTENANCE'))               { maint     += count; continue; }
    other += count;
  }

  // Order must match component's pieCategories array:
  // ['No response', 'Grid alarms', 'DG alarms', 'Low PF', 'Timeout', 'Maintenance', 'Other']
  const values = [noResp, gridCount, dgCount, lowPf, timeout, maint, other];

  return categories.map((cat, i) => ({
    label: cat.label,
    value: values[i] ?? 0,
    color: cat.color,
  }));
}

// Raw shape from the ALARM_ID_LIST API
export interface AlarmTimeRow {
  alarm_id:   string;
  alarm_time: string;
  count:      string | number;
}

// ─────────────────────────────────────────────────────────────────────────────
// buildTrendChartFromTimeRows()
//
// Groups rows into 24 hourly buckets across Grid / DG / Others.
// Pass fromTime / toTime to restrict the x-axis to only the selected range.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Round time up to the next hour.
 * Examples: 00:00-01:59 → 2, 01:10 → 2, 02:00 → 3, 02:01 → 3
 */
function roundTimeToNextHour(date: Date): number {
  const hour = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  
  // If there are any minutes or seconds, round up to next hour
  if (minutes > 0 || seconds > 0) {
    return hour + 1;
  }
  // If exactly on the hour (00:00), still round up
  return hour + 1;
}

export function buildTrendChartFromTimeRows(
  rows:     AlarmTimeRow[],
  fromTime: string,   // "2022-12-16 00:00:00"
  toTime:   string,   // "2022-12-16 23:59:59"
): any {
  const from = new Date(fromTime.replace(' ', 'T'));
  const to   = new Date(toTime.replace(' ', 'T'));

  const fromHour  = from.getHours();
  const toHour    = to.getHours();
  const slotCount = toHour - fromHour + 1;

  const grid   = new Array(slotCount).fill(0);
  const dg     = new Array(slotCount).fill(0);
  const others = new Array(slotCount).fill(0);

  for (const row of rows) {
    const d = new Date(row.alarm_time.replace(' ', 'T'));
    if (isNaN(d.getTime()) || d < from || d > to) continue;

    // Round time up to next hour
    const roundedHour = roundTimeToNextHour(d);
    const slot = roundedHour - fromHour - 1; // -1 because we rounded up
    
    if (slot < 0 || slot >= slotCount) continue;

    const count = toNum(row.count);
    const upper = normaliseAlarmId(row.alarm_id).toUpperCase();

    if (GRID_ALARM_IDS.has(upper))      grid[slot]   += count;
    else if (DG_ALARM_IDS.has(upper))   dg[slot]     += count;
    else                                others[slot] += count;
  }

  const hours = Array.from({ length: slotCount }, (_, i) =>
    `${String(fromHour + i + 1).padStart(2, '0')}:00`  // +1 to show rounded hour
  );

  return {
    series: [
      { name: 'Grid',   data: grid   },
      { name: 'DG',     data: dg     },
      { name: 'Others', data: others },
    ],
    chart:      { type: 'line', height: '100%', width: '100%', toolbar: { show: false }, zoom: { enabled: false } },
    xaxis:      { categories: hours, labels: { style: { fontSize: '10px', colors: '#64748b' }, rotate: -45 }, axisBorder: { show: false } },
    yaxis:      { labels: { style: { fontSize: '10px', colors: '#64748b' } }, min: 0 },
    stroke:     { curve: 'smooth', width: 2 },
    colors:     ['#3b82f6', '#f59e0b', '#ef4444'],
    fill:       { type: 'gradient', gradient: { opacityFrom: 0.07, opacityTo: 0.01 } },
    grid:       { borderColor: '#e2e8f0', strokeDashArray: 3 },
    tooltip:    { shared: true, intersect: false },
    markers:    { size: 2.5, strokeWidth: 0 },
    dataLabels: { enabled: false },
    legend:     { show: false },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PROGRESSIVE LOADING UTILITIES
// For handling large datasets (300k+ records) with gradual loading
// ─────────────────────────────────────────────────────────────────────────────

export interface ProgressiveLoadConfig {
  batchSize: number;        // Number of items to process per batch
  delayMs: number;          // Delay between batches (ms)
  onBatchComplete?: (processedCount: number, totalCount: number) => void;
}

/**
 * Process large arrays in batches to prevent UI freezing.
 * Returns a Promise that resolves when all items are processed.
 * 
 * Usage example:
 * ```typescript
 * const largeDataset = [...]; // 300k records
 * const result: SomeType[] = [];
 * 
 * await processInBatches(largeDataset, {
 *   batchSize: 5000,
 *   delayMs: 10,
 *   onBatchComplete: (processed, total) => {
 *     console.log(`Loaded ${processed}/${total} items`);
 *     // Update UI to show loading progress
 *   }
 * }, (item, index) => {
 *   // Process each item
 *   const processed = transformItem(item);
 *   result.push(processed);
 * });
 * ```
 */
export async function processInBatches<T>(
  items: T[],
  config: ProgressiveLoadConfig,
  processor: (item: T, index: number) => void
): Promise<void> {
  const { batchSize, delayMs, onBatchComplete } = config;
  const total = items.length;
  
  for (let i = 0; i < total; i += batchSize) {
    const end = Math.min(i + batchSize, total);
    
    // Process batch
    for (let j = i; j < end; j++) {
      processor(items[j], j);
    }
    
    // Notify progress
    if (onBatchComplete) {
      onBatchComplete(end, total);
    }
    
    // Yield to browser for UI update
    if (end < total) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
}

/**
 * Build trend chart with progressive loading for large datasets.
 * Returns a chart config that updates progressively as data is processed.
 * 
 * Usage in component:
 * ```typescript
 * this.chartOptions = buildTrendChartFromTimeRowsProgressive(
 *   rows,
 *   fromTime,
 *   toTime,
 *   {
 *     batchSize: 10000,
 *     delayMs: 20,
 *     onBatchComplete: (processed, total) => {
 *       this.loadingProgress = Math.round((processed / total) * 100);
 *       // Trigger chart update
 *       this.updateChartData();
 *     }
 *   }
 * );
 * ```
 */
export async function buildTrendChartFromTimeRowsProgressive(
  rows:     AlarmTimeRow[],
  fromTime: string,
  toTime:   string,
  config:   ProgressiveLoadConfig
): Promise<any> {
  const from = new Date(fromTime.replace(' ', 'T'));
  const to   = new Date(toTime.replace(' ', 'T'));

  const fromHour  = from.getHours();
  const toHour    = to.getHours();
  const slotCount = toHour - fromHour + 1;

  const grid   = new Array(slotCount).fill(0);
  const dg     = new Array(slotCount).fill(0);
  const others = new Array(slotCount).fill(0);

  // Process rows in batches
  await processInBatches(rows, config, (row) => {
    const d = new Date(row.alarm_time.replace(' ', 'T'));
    if (isNaN(d.getTime()) || d < from || d > to) return;

    const roundedHour = roundTimeToNextHour(d);
    const slot = roundedHour - fromHour - 1;
    
    if (slot < 0 || slot >= slotCount) return;

    const count = toNum(row.count);
    const upper = normaliseAlarmId(row.alarm_id).toUpperCase();

    if (GRID_ALARM_IDS.has(upper))      grid[slot]   += count;
    else if (DG_ALARM_IDS.has(upper))   dg[slot]     += count;
    else                                others[slot] += count;
  });

  const hours = Array.from({ length: slotCount }, (_, i) =>
    `${String(fromHour + i + 1).padStart(2, '0')}:00`
  );

  return {
    series: [
      { name: 'Grid',   data: grid   },
      { name: 'DG',     data: dg     },
      { name: 'Others', data: others },
    ],
    chart:      { type: 'line', height: '100%', width: '100%', toolbar: { show: false }, zoom: { enabled: false } },
    xaxis:      { categories: hours, labels: { style: { fontSize: '10px', colors: '#64748b' }, rotate: -45 }, axisBorder: { show: false } },
    yaxis:      { labels: { style: { fontSize: '10px', colors: '#64748b' } }, min: 0 },
    stroke:     { curve: 'smooth', width: 2 },
    colors:     ['#3b82f6', '#f59e0b', '#ef4444'],
    fill:       { type: 'gradient', gradient: { opacityFrom: 0.07, opacityTo: 0.01 } },
    grid:       { borderColor: '#e2e8f0', strokeDashArray: 3 },
    tooltip:    { shared: true, intersect: false },
    markers:    { size: 2.5, strokeWidth: 0 },
    dataLabels: { enabled: false },
    legend:     { show: false },
  };
}